/**
 * Generic Request Deduplication Service
 * 
 * Prevents multiple identical requests from being made simultaneously.
 * If a request is already pending, new requests return the same promise.
 * 
 * Usage:
 *   const result = await requestCacheService.getOrFetch(
 *     'unique-key',
 *     () => fetch('/api/endpoint').then(r => r.json())
 *   );
 */

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('REQUEST_CACHE');

interface CacheEntry<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestCacheService {
  private pendingRequests = new Map<string, CacheEntry<any>>();

  /**
   * Get cached result or fetch using provided function
   * If a request with this key is already pending, returns the same promise
   * 
   * @param key Unique key for this request
   * @param fetchFn Function to call if not already pending
   */
  async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>
  ): Promise<T> {
    // Check if already pending
    const pending = this.pendingRequests.get(key);
    if (pending) {
      log.debug(`[RequestCache] Cache hit (deduplication) for key: ${key}`);
      return pending.promise;
    }

    // Not pending, create new request
    log.debug(`[RequestCache] Creating new request for key: ${key}`);
    const promise = fetchFn()
      .then(result => {
        // Remove from pending after a small delay to catch concurrent requests
        setTimeout(() => {
          this.pendingRequests.delete(key);
          log.debug(`[RequestCache] Completed and removed from pending: ${key}`);
        }, 50);
        return result;
      })
      .catch(error => {
        // Remove from pending on error
        this.pendingRequests.delete(key);
        log.debug(`[RequestCache] Failed and removed from pending: ${key}`);
        throw error;
      });

    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    return promise;
  }

  /**
   * Invalidate cache entry by key or pattern
   * @param key String key or RegExp pattern
   */
  invalidate(key: string | RegExp): void {
    if (typeof key === 'string') {
      this.pendingRequests.delete(key);
      log.debug(`[RequestCache] Invalidated key: ${key}`);
    } else {
      // RegExp - invalidate all matching keys
      const keysToDelete: string[] = [];
      Array.from(this.pendingRequests.keys()).forEach(cacheKey => {
        if (key.test(cacheKey)) {
          keysToDelete.push(cacheKey);
        }
      });
      
      keysToDelete.forEach(k => {
        this.pendingRequests.delete(k);
        log.debug(`[RequestCache] Invalidated pattern match: ${k}`);
      });
    }
  }

  /**
   * Clear all pending requests
   */
  clear(): void {
    this.pendingRequests.clear();
    log.debug('[RequestCache] Cleared all cached requests');
  }

  /**
   * Get current number of pending requests (useful for debugging)
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

export const requestCacheService = new RequestCacheService();

// Listen for entity updates and invalidate related caches
import { entityEventService } from './entityEventService';

entityEventService.onEntityEvent('updated', (event) => {
  // Invalidate any cached requests for this entity
  requestCacheService.invalidate(new RegExp(`/api/entities/${event.entity._id}`));
  log.debug('[RequestCacheService] Cache invalidated for entity:', event.entity._id);
});
